/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  MediaStreamType,
  PlayerMedia,
  PlayerPlayInfo,
} from "../../shared/types";
import { usePlayerFeatures } from "../BasePlayer";
import { useCustomPlayerContext } from "../CustomPlayerContext";

type PlayerCurrentMediaContextValue = {
  streamType: MediaStreamType;
  playInfo: PlayerPlayInfo;
  media: PlayerMedia;
  nextMedia: PlayerMedia | undefined;
  loadNextMedia: () => void;
};

type PlayerCurrentMediaProviderProps = Omit<
  PlayerCurrentMediaContextValue,
  "nextMedia" | "loadNextMedia"
> & {
  children: ReactNode;
};

const PlayerCurrentMediaContext = createContext<PlayerCurrentMediaContextValue>(
  {} as PlayerCurrentMediaContextValue,
);

export const PlayerCurrentMediaProvider = (
  props: PlayerCurrentMediaProviderProps,
) => {
  const { media, playInfo, streamType, children } = props;

  const { nextEpisode: nextEpisodeEnabled } = usePlayerFeatures();
  const { onFetchMedia, onNavigateToMedia } = useCustomPlayerContext();

  const [nextMedia, setNextMedia] = useState<PlayerMedia | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!playInfo.NextMediaId || !nextEpisodeEnabled || !onFetchMedia) {
      setNextMedia(undefined);
      return;
    }

    let mounted = true;
    onFetchMedia(playInfo.NextMediaId)
      .then((media) => mounted && setNextMedia(media))
      .catch(() => mounted && setNextMedia(undefined));

    return () => {
      mounted = false;
    };
  }, [playInfo.NextMediaId, nextEpisodeEnabled, onFetchMedia]);

  const loadNextMedia = useCallback(() => {
    nextEpisodeEnabled && nextMedia && onNavigateToMedia?.(nextMedia);
  }, [nextEpisodeEnabled, nextMedia, onNavigateToMedia]);

  return (
    <PlayerCurrentMediaContext.Provider
      value={{ streamType, playInfo, media, nextMedia, loadNextMedia }}
    >
      {children}
    </PlayerCurrentMediaContext.Provider>
  );
};

export const usePlayerCurrentMedia = (): PlayerCurrentMediaContextValue => {
  const context = useContext(PlayerCurrentMediaContext);

  if (!context) {
    throw new Error("Component beyond PlayerCurrentMediaContext");
  }

  return context;
};
